import React, { useEffect } from 'react';
import { Check } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { pricingData } from '../utils/mockData';

const Estimates = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white">
      {/* Header */}
      <section className="bg-gradient-to-r from-cyan-600 to-emerald-600 py-10">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl lg:text-5xl font-bold text-white mb-4">
            Transparent Pricing
          </h1>
          <p className="text-xl text-cyan-50 max-w-2xl mx-auto">
            No hidden fees, no surprises. Just honest, competitive pricing for quality service.
          </p>
        </div>
      </section>

      {/* Window Cleaning Pricing */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-2">Window Cleaning Prices</h2>
            <p className="text-gray-600">Professional external window cleaning services</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto">
            {pricingData.windowCleaning.map((item, index) => (
              <Card 
                key={item.id} 
                className={`border-2 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 ${
                  index === 1
                    ? 'border-cyan-500 relative' 
                    : 'border-gray-200 hover:border-cyan-300'
                }`}
              >
                {index === 1 && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <Badge className="bg-gradient-to-r from-cyan-500 to-emerald-500 text-white px-4 py-1 text-sm">
                      Most Popular
                    </Badge>
                  </div>
                )}
                <CardHeader className="text-center pb-4">
                  <CardTitle className="text-xl font-bold text-gray-900 mb-2">
                    {item.category}
                  </CardTitle>
                  <div className="mb-2">
                    <span className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-cyan-600 to-emerald-600">
                      {item.price}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600">{item.description}</p>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3">
                    {item.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start space-x-3">
                        <div className="flex-shrink-0 w-5 h-5 rounded-full bg-emerald-100 flex items-center justify-center mt-0.5">
                          <Check className="w-3 h-3 text-emerald-600" />
                        </div>
                        <span className="text-sm text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* What's Included Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <Card className="border-2 border-cyan-100 bg-gradient-to-br from-cyan-50 to-emerald-50">
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-4 text-center">
                  What's Included in Every Service
                </h3>
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-cyan-500 flex items-center justify-center">
                      <Check className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">External Windows</p>
                      <p className="text-sm text-gray-600">Professional external window cleaning</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-cyan-500 flex items-center justify-center">
                      <Check className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">Sills & Frames</p>
                      <p className="text-sm text-gray-600">UPVC cleaned and washed down</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-cyan-500 flex items-center justify-center">
                      <Check className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">Purified Water</p>
                      <p className="text-sm text-gray-600">Streak-free finish guaranteed</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-cyan-500 flex items-center justify-center">
                      <Check className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">Fully Insured</p>
                      <p className="text-sm text-gray-600">Peace of mind protection</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Gutter Clearing Pricing */}
      <section className="py-16 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-2">Gutter Clearing Prices</h2>
            <p className="text-gray-600">Emptying gutters using ground-level gutter vac system - no ladders required</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {pricingData.gutterClearing.map((item, index) => (
              <Card 
                key={item.id} 
                className={`border-2 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 ${
                  index === 1
                    ? 'border-emerald-500 relative' 
                    : 'border-gray-200 hover:border-emerald-300'
                }`}
              >
                {index === 1 && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <Badge className="bg-gradient-to-r from-emerald-500 to-cyan-500 text-white px-4 py-1 text-sm">
                      Most Popular
                    </Badge>
                  </div>
                )}
                <CardHeader className="text-center pb-4">
                  <CardTitle className="text-xl font-bold text-gray-900 mb-2">
                    {item.category}
                  </CardTitle>
                  <div className="mb-2">
                    <span className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-emerald-600 to-cyan-600">
                      {item.price}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600">{item.description}</p>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3">
                    {item.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start space-x-3">
                        <div className="flex-shrink-0 w-5 h-5 rounded-full bg-emerald-100 flex items-center justify-center mt-0.5">
                          <Check className="w-3 h-3 text-emerald-600" />
                        </div>
                        <span className="text-sm text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Gutter Cleaning Pricing */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-2">Gutter Cleaning Prices</h2>
            <p className="text-gray-600">Washing down guttering, fascia boards and downpipes</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {pricingData.gutterCleaning.map((item, index) => (
              <Card 
                key={item.id} 
                className={`border-2 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 ${
                  index === 1
                    ? 'border-cyan-500 relative' 
                    : 'border-gray-200 hover:border-cyan-300'
                }`}
              >
                {index === 1 && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <Badge className="bg-gradient-to-r from-cyan-500 to-emerald-500 text-white px-4 py-1 text-sm">
                      Most Popular
                    </Badge>
                  </div>
                )}
                <CardHeader className="text-center pb-4">
                  <CardTitle className="text-xl font-bold text-gray-900 mb-2">
                    {item.category}
                  </CardTitle>
                  <div className="mb-2">
                    <span className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-cyan-600 to-emerald-600">
                      {item.price}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600">{item.description}</p>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3">
                    {item.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start space-x-3">
                        <div className="flex-shrink-0 w-5 h-5 rounded-full bg-cyan-100 flex items-center justify-center mt-0.5">
                          <Check className="w-3 h-3 text-cyan-600" />
                        </div>
                        <span className="text-sm text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
          
          {/* Discount Note */}
          <div className="max-w-5xl mx-auto mt-8">
            <Card className="border-2 border-emerald-200 bg-gradient-to-r from-emerald-50 to-cyan-50">
              <CardContent className="p-6 text-center">
                <p className="text-lg font-semibold text-gray-900">
                  💰 Save Money: Book both Gutter Clearing AND Gutter Cleaning together and receive a discount on both services!
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Pricing Disclaimer */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <Card className="border-2 border-gray-300 bg-gray-50">
              <CardContent className="p-8">
                <h3 className="text-xl font-bold text-gray-900 mb-4 text-center">Important Pricing Information</h3>
                <p className="text-gray-700 text-center leading-relaxed mb-4">
                  Please note that all prices shown above are estimates and should be used as a guide only. The actual cost for your property may vary depending on factors such as property size, accessibility, condition of windows/gutters, and specific requirements.
                </p>
                <p className="text-gray-700 text-center leading-relaxed">
                  Conservatories, extensions, bi-fold doors and velux windows will add a little extra onto the total cost of window cleaning. To obtain an accurate quote tailored to your property, please get in touch with us directly. We'll be happy to provide a free, no-obligation quote.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Additional Info Section */}
      <section className="py-16 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto px-4">
          {/* Notes */}
          <div className="max-w-4xl mx-auto">
            <Card className="border border-gray-200">
              <CardContent className="p-6">
                <h4 className="font-semibold text-gray-900 mb-3">Important Notes:</h4>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-start">
                    <span className="mr-2">•</span>
                    <span>Prices shown are standard rates and may vary based on property condition and accessibility</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2">•</span>
                    <span>Window cleaning prices above are for regular 4 or 8 weekly maintenance. One off and first cleans are charged at a slightly higher rate due to additional time required</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2">•</span>
                    <span>Free quotes available to both residential and commercial customers</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2">•</span>
                    <span>All work is fully guaranteed - if you're not happy, we'll return free of charge</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>

          {/* CTA */}
          <div className="mt-12 text-center">
            <div className="inline-block bg-gradient-to-r from-cyan-600 to-emerald-600 rounded-2xl p-8 text-white">
              <h3 className="text-2xl font-bold mb-3">Ready to Get Started?</h3>
              <p className="mb-6 text-cyan-50">Contact us today for a free, no-obligation quote</p>
              <a
                href="/contact"
                className="inline-block bg-white text-cyan-600 font-semibold px-8 py-3 rounded-lg hover:bg-gray-100 transition-colors"
              >
                Get Your Free Quote
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Estimates;
